/**
 * Persistent dashboard banner that nudges the user to set up their
 * successor — the single person with full access who will execute
 * their wishes.
 *
 * Three states, driven by the backend's `getSuccessorStatus`:
 *
 *   - 'active'  → banner hidden. User has an accepted successor.
 *   - 'pending' → banner shows "Invitation to <Name> is pending" copy
 *                 and taps through to the Linked Accounts screen (NOT
 *                 the Add Successor form). The user has already done
 *                 their part; this is an informational nudge, not a
 *                 setup prompt.
 *   - 'none'    → banner shows the "Choose your successor" CTA and
 *                 taps to Add Successor (with the wizard seed
 *                 pre-filled when available).
 *
 * Older backends only return `hasSuccessor` boolean (no successorStatus
 * field). We treat that as the legacy two-state shape: hidden when
 * hasSuccessor=true, "Choose your successor" prompt otherwise.
 *
 * Refreshes when the dashboard regains focus so the banner reflects the
 * latest state after the user completes any successor action.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ms } from '../utils/responsive';
import { onboardingService } from '../services/onboarding';

type Person = { firstName?: string; email?: string } | null;
type Status = 'active' | 'pending' | 'none' | null;

export const SuccessorPromptBanner: React.FC = () => {
  const navigation = useNavigation<any>();
  const [status, setStatus] = useState<Status>(null);
  const [seed, setSeed] = useState<Person>(null);
  const [pendingInvitee, setPendingInvitee] = useState<Person>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        const result = await onboardingService.getSuccessorStatus();
        if (cancelled) return;
        // Prefer the explicit 3-state field when present, otherwise
        // fall back to the boolean for legacy backends.
        const next: Status =
          result.successorStatus ??
          (result.hasSuccessor ? 'active' : 'none');
        setStatus(next);
        setSeed(result.seed);
        setPendingInvitee(result.pendingInvitee ?? null);
      })();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  // Hide while loading and when the user has an active successor.
  if (status === null || status === 'active') return null;

  const isPending = status === 'pending';

  const handlePress = () => {
    if (isPending) {
      // The user has already submitted the form; an invitation is
      // outstanding. Route them to the Linked Accounts screen so they
      // can see / resend / cancel — NOT back to Add Successor where
      // they'd re-fill the same form they just submitted.
      navigation.navigate('Profile', { screen: 'LinkedAccounts' });
      return;
    }
    navigation.navigate('Profile', {
      screen: 'AddSuccessor',
      params: seed
        ? {
            prefilledData: {
              firstName: seed.firstName,
              email: seed.email,
            },
          }
        : undefined,
    });
  };

  const inviteeName =
    pendingInvitee?.firstName ||
    pendingInvitee?.email ||
    'your invitee';

  const eyebrow = isPending ? 'Invitation pending' : 'Set up needed';
  const title = isPending
    ? `Waiting for ${inviteeName} to accept`
    : seed?.firstName
    ? `Finish setting up ${seed.firstName}`
    : 'Choose your successor';
  const subtitle = isPending
    ? 'Your successor invitation has been sent. Tap to view, resend, or cancel.'
    : seed?.firstName
    ? 'Add their phone, a secret passcode, and a validation question so they can unlock your vault.'
    : 'Pick the one person who gets full access to your vault and carries out your wishes.';

  return (
    <TouchableOpacity
      style={styles.banner}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <View style={{ flex: 1 }}>
        <Text style={styles.eyebrow}>{eyebrow}</Text>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>
      </View>
      <Text style={styles.arrow}>→</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1C2C4F',
    marginHorizontal: ms(12),
    marginTop: ms(10),
    marginBottom: ms(4),
    padding: ms(14),
    borderRadius: ms(12),
  },
  eyebrow: {
    fontSize: ms(10, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: ms(3),
  },
  title: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: ms(2),
  },
  subtitle: {
    fontSize: ms(12, 0.3),
    color: '#C6D2E3',
    lineHeight: ms(17, 0.3),
  },
  arrow: {
    fontSize: ms(22, 0.3),
    color: '#29BFC0',
    marginLeft: ms(10),
    fontWeight: '700',
  },
});
